/**
 * Area Tool - Area Creation
 *
 * Two real placement flows:
 * 1. Automatic boundary detection — a single click inside a room enclosed
 *    by walls finds the smallest wall-endpoint cycle around the click and
 *    creates the area from that real boundary.
 * 2. Manual sketch — when no enclosed boundary exists, clicks accumulate
 *    polygon points (Enter closes, Escape clears).
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface AreaGeometryData {
    position: [number, number, number];
    levelId: string;
    boundary?: [number, number][];
}

export interface AreaElement extends Element {
    category: 'Areas';
    geometryData: AreaGeometryData;
}

/** Endpoint merge tolerance for the wall connectivity graph (metres). */
const NODE_MERGE_TOLERANCE = 0.05;
/** Maximum click-to-wall distance for auto-detection to engage (metres). */
const PICK_RADIUS = 1.0;

export class AreaTool extends BaseTool {
    id = 'area';
    name = 'Area';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);

        // Automatic boundary detection first: an enclosed room is picked in
        // one click; otherwise the click starts a manual sketch.
        const detected = this.detectEnclosedBoundary(snapped);
        if (detected) {
            this.createArea(detected);
            this.exitToSelect();
            return;
        }

        this.points.push(snapped);

        // Close shape?
        if (this.points.length > 2) {
            const start = this.points[0];
            if (snapped.distanceTo(start) < 0.2) {
                this.points.pop(); // Remove last duplicate
                this.finishSketch();
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.code === 'Enter') {
             if (this.points.length > 2) this.finishSketch();
        } else if (event.code === 'Escape') {
            this.points = [];
            this.updatePreview();
        }
    }

    private updatePreview() {
         if (!this.context) return;
         if (this.points.length > 0 && this.currentPoint) {
             const pts = [...this.points, this.currentPoint];
             this.context.appStore.setToolPreview({
                 type: 'polyline',
                 points: pts,
                 color: '#ffdd00' // Area Color
             });
         } else {
             this.context.appStore.setToolPreview(null);
         }
    }

    onPointerUp(): void {}

    // ==================== Automatic boundary detection ====================

    /**
     * Find the smallest wall cycle enclosing `point`: wall endpoints are
     * clustered into graph nodes, the shortest cycle through the node
     * nearest the click is searched per incident edge, and the winning loop
     * must contain the click (ray-cast point-in-polygon).
     */
    private detectEnclosedBoundary(point: THREE.Vector3): [number, number][] | null {
        const walls = Array.from(this.context!.bimStore.project.elements.values())
            .filter((el: any) => el.category === 'Walls' && el.geometryData?.start && el.geometryData?.end) as any[];
        if (walls.length < 3) return null;

        // Cluster endpoints into graph nodes.
        const nodes: [number, number][] = [];
        const adjacency: number[][] = [];
        const nodeOf = (x: number, y: number): number => {
            for (let i = 0; i < nodes.length; i++) {
                if (Math.hypot(nodes[i][0] - x, nodes[i][1] - y) < NODE_MERGE_TOLERANCE) return i;
            }
            nodes.push([x, y]);
            adjacency.push([]);
            return nodes.length - 1;
        };
        for (const wall of walls) {
            const a = nodeOf(wall.geometryData.start[0], wall.geometryData.start[1]);
            const b = nodeOf(wall.geometryData.end[0], wall.geometryData.end[1]);
            if (a === b) continue;
            if (!adjacency[a].includes(b)) adjacency[a].push(b);
            if (!adjacency[b].includes(a)) adjacency[b].push(a);
        }

        // Start from the node nearest the click.
        let start = -1;
        let bestDist = Infinity;
        nodes.forEach(([x, y], i) => {
            const d = Math.hypot(x - point.x, y - point.y);
            if (d < bestDist) {
                bestDist = d;
                start = i;
            }
        });
        if (start < 0 || bestDist > PICK_RADIUS) return null;

        // Shortest cycle through `start`: BFS from each incident edge back
        // to `start` without reusing that edge.
        let bestCycle: number[] | null = null;
        for (const firstNeighbor of adjacency[start]) {
            const parent = new Map<number, number>([[firstNeighbor, -1]]);
            const queue: number[] = [firstNeighbor];
            let found = false;
            while (queue.length > 0 && !found) {
                const cur = queue.shift()!;
                for (const nb of adjacency[cur]) {
                    if (cur === firstNeighbor && nb === start) continue; // keep the start edge out
                    if (parent.has(nb)) continue;
                    parent.set(nb, cur);
                    if (nb === start) {
                        found = true;
                        break;
                    }
                    queue.push(nb);
                }
            }
            if (!found) continue;
            const cycle: number[] = [start];
            let cur = parent.get(start)!;
            while (cur !== -1) {
                cycle.push(cur);
                cur = parent.get(cur)!;
            }
            if (cycle.length < 3) continue; // parallel-wall sliver, not a room
            if (!bestCycle || cycle.length < bestCycle.length) bestCycle = cycle;
        }

        if (!bestCycle) return null;
        const polygon = bestCycle.map((i) => nodes[i]);
        if (!pointInPolygon(point.x, point.y, polygon)) return null;
        return polygon;
    }

    /** Create the area element from a real boundary polygon. */
    private createArea(boundary: [number, number][]) {
        if (!this.context) return;
        const levelId = 'Level 1';

        const verts = boundary.map(([x, y]) => new THREE.Vector3(x, y, 0));
        const center = this.calculateCentroid(verts);
        const areaVal = this.calculateArea(verts);

        const geometryData: AreaGeometryData = {
            position: [center.x, center.y, center.z],
            levelId,
            boundary
        };

        const area: AreaElement = {
            id: uuidv4(),
            category: 'Areas',
            type: 'Area',
            parameters: {
                'Level': levelId,
                'Name': 'Area',
                'Area': `${areaVal.toFixed(2)} m²`
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(area);
    }

    private finishSketch() {
        if (!this.context) return;
        const boundary = this.points.map(p => [p.x, p.y] as [number, number]);
        this.createArea(boundary);
        this.exitToSelect();
        this.points = [];
        this.currentPoint = null;
        this.context.appStore.setToolPreview(null);
    }

    private calculateCentroid(points: THREE.Vector3[]) {
        let x = 0, y = 0, z = 0;
        points.forEach(p => { x += p.x; y += p.y; z += p.z });
        return new THREE.Vector3(x/points.length, y/points.length, z/points.length);
    }

    private calculateArea(points: THREE.Vector3[]) {
        let area = 0;
        for (let i = 0; i < points.length; i++) {
            const j = (i + 1) % points.length;
            area += points[i].x * points[j].y;
            area -= points[j].x * points[i].y;
        }
        return Math.abs(area) / 2;
    }
}

/** Standard ray-cast point-in-polygon test (XY plane). */
function pointInPolygon(x: number, y: number, polygon: [number, number][]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];
        const intersects = (yi > y) !== (yj > y) &&
            x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
        if (intersects) inside = !inside;
    }
    return inside;
}
